/**
 * Statistics Modal
 * 
 * 평가 통계를 시각화하는 모달
 * - 일별 평가 개수: 꺾은선 그래프 (Line Chart)
 * - 상태별 분포: 도넛 차트 (Donut Chart)
 */

import { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../i18n';
import { useBodyScrollLock } from '../hooks';
import { getStatistics, type StatisticsResponse } from '../api';

interface StatisticsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// 상태별 색상
const STATUS_COLORS = {
  go: '#10b981',        // 초록
  conditional: '#f59e0b', // 주황
  holding: '#f97316',    // 진한 주황
  no_go: '#ef4444',      // 빨강
};

// 상태별 라벨
const STATUS_LABELS = {
  ko: {
    go: 'GO',
    conditional: 'Conditional',
    holding: 'Holding',
    no_go: 'No-Go',
  },
  en: {
    go: 'GO',
    conditional: 'Conditional',
    holding: 'Holding',
    no_go: 'No-Go',
  },
};

export default function StatisticsModal({ isOpen, onClose }: StatisticsModalProps) {
  const { lang, t } = useLanguage();
  const [stats, setStats] = useState<StatisticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [periodDays, setPeriodDays] = useState(30);

  // 통계 데이터 로드
  useEffect(() => {
    if (isOpen) {
      loadStatistics();
    }
  }, [isOpen, periodDays]);

  const loadStatistics = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getStatistics(periodDays);
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.statistics?.loadError || 'Failed to load statistics');
    } finally {
      setIsLoading(false);
    }
  };

  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const isKo = lang === 'ko';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex-shrink-0 border-b border-terminal-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-terminal-text flex items-center gap-2">
              📊 {t.statistics?.title || (isKo ? '평가 통계' : 'Evaluation Statistics')}
            </h2>
            <p className="text-sm text-terminal-muted mt-1">
              {t.statistics?.subtitle?.replace('{days}', String(periodDays)) || 
                (isKo ? `최근 ${periodDays}일간 데이터` : `Data from the last ${periodDays} days`)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* 기간 선택 */}
            <select
              value={periodDays}
              onChange={(e) => setPeriodDays(Number(e.target.value))}
              className="bg-terminal-dim/30 border border-terminal-border rounded-lg px-3 py-1.5 text-sm text-terminal-text"
            >
              <option value={7}>{isKo ? '7일' : '7 days'}</option>
              <option value={14}>{isKo ? '14일' : '14 days'}</option>
              <option value={30}>{isKo ? '30일' : '30 days'}</option>
              <option value={60}>{isKo ? '60일' : '60 days'}</option>
              <option value={90}>{isKo ? '90일' : '90 days'}</option>
            </select>
            <button
              onClick={onClose}
              className="p-2 hover:bg-terminal-dim/30 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* 로딩 */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-status-go/30 border-t-status-go rounded-full animate-spin"></div>
            </div>
          )}

          {/* 에러 */}
          {error && !isLoading && (
            <div className="p-4 bg-status-danger/10 border border-status-danger/30 rounded-lg text-status-danger text-sm text-center">
              {error}
              <button
                onClick={loadStatistics}
                className="block mx-auto mt-2 text-xs underline hover:no-underline"
              >
                {isKo ? '다시 시도' : 'Retry'}
              </button>
            </div>
          )}

          {/* 통계 데이터 */}
          {!isLoading && !error && stats && (
            <div className="space-y-8">
              {/* 요약 카드 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SummaryCard
                  icon="📋"
                  label={isKo ? '총 평가' : 'Total Evaluations'}
                  value={stats.total_evaluations}
                />
                <SummaryCard
                  icon="📊"
                  label={isKo ? '평균 점수' : 'Avg Score'}
                  value={stats.average_score !== null ? stats.average_score : '-'}
                  suffix={stats.average_score !== null ? '점' : ''}
                />
                <SummaryCard
                  icon="✅"
                  label="GO"
                  value={stats.status_distribution.go}
                  color="text-status-go"
                />
                <SummaryCard
                  icon="⚠️"
                  label={isKo ? 'No-Go / Hold' : 'No-Go / Hold'}
                  value={stats.status_distribution.no_go + stats.status_distribution.holding}
                  color="text-status-danger"
                />
              </div>

              {/* 차트 영역 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 일별 평가 추이 - 꺾은선 그래프 */}
                <div className="bg-terminal-dim/10 border border-terminal-border rounded-xl p-5">
                  <h3 className="text-sm font-bold text-terminal-text mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 bg-status-go rounded-full"></span>
                    {isKo ? '일별 평가 추이' : 'Daily Evaluation Trend'}
                  </h3>
                  <LineChart 
                    data={stats.daily_evaluations} 
                    lang={lang}
                  />
                </div>

                {/* 상태별 분포 - 도넛 차트 */}
                <div className="bg-terminal-dim/10 border border-terminal-border rounded-xl p-5">
                  <h3 className="text-sm font-bold text-terminal-text mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 bg-status-warning rounded-full"></span>
                    {isKo ? '상태별 분포' : 'Status Distribution'}
                  </h3>
                  <DonutChart 
                    data={stats.status_distribution}
                    total={stats.total_evaluations}
                    lang={lang}
                  />
                </div>
              </div>

              {/* 주요 키워드 랭킹 */}
              {stats.top_keywords && stats.top_keywords.length > 0 && (
                <div className="bg-terminal-dim/10 border border-terminal-border rounded-xl p-5">
                  <h3 className="text-sm font-bold text-terminal-text mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#8b5cf6] rounded-full"></span>
                    {isKo ? '주요 키워드 TOP 10' : 'Top 10 Keywords'}
                  </h3>
                  <KeywordRanking 
                    keywords={stats.top_keywords}
                    lang={lang}
                  />
                </div>
              )}
            </div>
          )}

          {/* 데이터 없음 */}
          {!isLoading && !error && stats && stats.total_evaluations === 0 && (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">📊</div>
              <p className="text-terminal-muted">
                {isKo ? '아직 평가 데이터가 없습니다.' : 'No evaluation data yet.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// 요약 카드 컴포넌트
function SummaryCard({ 
  icon, 
  label, 
  value, 
  suffix = '',
  color = 'text-terminal-text'
}: { 
  icon: string; 
  label: string; 
  value: number | string; 
  suffix?: string;
  color?: string;
}) {
  return (
    <div className="bg-terminal-dim/10 border border-terminal-border rounded-lg p-4">
      <div className="flex items-center gap-2 text-terminal-muted text-xs mb-2">
        <span>{icon}</span>
        <span>{label}</span>
      </div>
      <div className={`text-2xl font-bold ${color}`}>
        {value}{suffix && <span className="text-sm text-terminal-muted ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

// 꺾은선 그래프 컴포넌트
function LineChart({ 
  data, 
  lang 
}: { 
  data: { date: string; count: number }[];
  lang: string;
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const chartWidth = 400;
  const chartHeight = 200;
  const padding = { top: 20, right: 20, bottom: 40, left: 40 };
  
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // 데이터 처리
  const processedData = useMemo(() => {
    if (data.length === 0) return { points: [], maxCount: 0, dates: [] };
    
    const rawMax = Math.max(...data.map(d => d.count), 0);
    // Y축 눈금: 5 단위(5의 배수)로 끊기 위해 최대값을 5의 배수로 올림
    const maxCount = Math.max(5, Math.ceil(rawMax / 5) * 5);
    const points = data.map((d, i) => ({
      x: padding.left + (i / (data.length - 1 || 1)) * innerWidth,
      y: padding.top + innerHeight - (d.count / maxCount) * innerHeight,
      count: d.count,
      date: d.date,
    }));
    
    return { points, maxCount, dates: data.map(d => d.date) };
  }, [data, innerWidth, innerHeight]);

  const { points, maxCount } = processedData;
  const isKo = lang === 'ko';

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-terminal-muted text-sm">
        {lang === 'ko' ? '데이터 없음' : 'No data'}
      </div>
    );
  }

  // 경로 생성
  const linePath = points.length > 0
    ? `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`
    : '';

  // 영역 경로 (그라데이션 채우기용)
  const areaPath = points.length > 0
    ? `M ${padding.left},${padding.top + innerHeight} L ${points.map(p => `${p.x},${p.y}`).join(' L ')} L ${padding.left + innerWidth},${padding.top + innerHeight} Z`
    : '';

  // Y축 눈금: 5 단위(최대값은 5의 배수로 올림)
  const yTicks = useMemo(() => {
    const step = 5;
    const ticks: number[] = [];
    for (let v = 0; v <= maxCount; v += step) ticks.push(v);
    return ticks;
  }, [maxCount]);

  // X축 라벨 (시작, 중간, 끝)
  const xLabels = useMemo(() => {
    if (data.length === 0) return [];
    if (data.length <= 7) {
      return data.map((d, i) => ({ 
        label: d.date.slice(5), // MM-DD
        x: points[i]?.x || 0 
      }));
    }
    const indices = [0, Math.floor(data.length / 2), data.length - 1];
    return indices.map(i => ({
      label: data[i].date.slice(5),
      x: points[i]?.x || 0
    }));
  }, [data, points]);

  return (
    <svg
      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
      className="w-full h-auto"
      preserveAspectRatio="xMidYMid meet"
      overflow="visible"
      onMouseLeave={() => setHoveredIndex(null)}
    >
      <defs>
        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      
      {/* 그리드 라인 */}
      {yTicks.map((tick, i) => (
        <g key={i}>
          <line
            x1={padding.left}
            y1={padding.top + innerHeight - (tick / maxCount) * innerHeight}
            x2={padding.left + innerWidth}
            y2={padding.top + innerHeight - (tick / maxCount) * innerHeight}
            stroke="currentColor"
            strokeWidth="0.5"
            strokeDasharray="4 4"
            className="text-terminal-border"
          />
          <text
            x={padding.left - 8}
            y={padding.top + innerHeight - (tick / maxCount) * innerHeight}
            textAnchor="end"
            dominantBaseline="middle"
            className="text-[10px] fill-current text-terminal-muted"
          >
            {tick}
          </text>
        </g>
      ))}
      
      {/* 영역 채우기 */}
      <path
        d={areaPath}
        fill="url(#lineGradient)"
        className="transition-all duration-500"
      />
      
      {/* 꺾은선 */}
      <path
        d={linePath}
        fill="none"
        stroke="#10b981"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="transition-all duration-500"
      />

      {/* 데이터 포인트 */}
      {points.map((point, i) => (
        <g key={i}>
          <circle
            cx={point.x}
            cy={point.y}
            r="4"
            fill="#10b981"
            stroke="white"
            strokeWidth="2"
            className="transition-all duration-300"
            onMouseEnter={() => setHoveredIndex(i)}
          />
        </g>
      ))}
      
      {/* X축 라벨 */}
      {xLabels.map((item, i) => (
        <text
          key={i}
          x={item.x}
          y={chartHeight - 10}
          textAnchor="middle"
          className="text-[10px] fill-current text-terminal-muted"
        >
          {item.label}
        </text>
      ))}

      {/* 툴팁 (호버된 포인트) - 맨 마지막에 렌더링하여 최상위에 표시 */}
      {hoveredIndex !== null && points[hoveredIndex] && (() => {
        const p = points[hoveredIndex];
        const labelDate = p.date.slice(5); // MM-DD
        const labelCount = isKo ? `${p.count}건` : `${p.count}`;
        const boxW = 54;
        const boxH = 32;
        const gap = 10; // 포인트와 툴팁 사이 간격
        
        // X 위치: 항상 포인트 중앙에 맞춤 (경계 보정 없음, overflow visible로 처리)
        const tooltipX = p.x - boxW / 2;
        
        // Y 위치: 포인트 바로 위에 표시
        const tooltipY = p.y - boxH - gap;

        return (
          <g>
            {/* 호버된 포인트 하이라이트 */}
            <circle
              cx={p.x}
              cy={p.y}
              r="7"
              fill="#10b981"
              opacity="0.3"
            />
            {/* 배경 박스 */}
            <rect
              x={tooltipX}
              y={tooltipY}
              width={boxW}
              height={boxH}
              rx="6"
              fill="rgba(15,23,42,0.95)"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="1"
            />
            {/* 화살표 */}
            <polygon
              points={`${p.x},${tooltipY + boxH + 5} ${p.x - 5},${tooltipY + boxH} ${p.x + 5},${tooltipY + boxH}`}
              fill="rgba(15,23,42,0.95)"
            />
            <text
              x={tooltipX + boxW / 2}
              y={tooltipY + 12}
              textAnchor="middle"
              className="text-[10px] fill-white/70"
            >
              {labelDate}
            </text>
            <text
              x={tooltipX + boxW / 2}
              y={tooltipY + 24}
              textAnchor="middle"
              className="text-[11px] fill-white font-bold"
            >
              {labelCount}
            </text>
          </g>
        );
      })()}
    </svg>
  );
}

// 도넛 차트 컴포넌트
function DonutChart({ 
  data, 
  total,
  lang 
}: { 
  data: { go: number; conditional: number; holding: number; no_go: number };
  total: number;
  lang: string;
}) {
  const [hovered, setHovered] = useState<{
    key: 'go' | 'conditional' | 'holding' | 'no_go';
    label: string;
    value: number;
    percentage: number;
    x: number;
    y: number;
  } | null>(null);
  const size = 200;
  const center = size / 2;
  const radius = 70;
  const strokeWidth = 24;

  // 데이터가 없으면 빈 차트 표시
  if (total === 0) {
    return (
      <div className="flex flex-col items-center">
        <svg width={size} height={size}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-terminal-dim"
          />
          <text
            x={center}
            y={center}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-sm fill-current text-terminal-muted"
          >
            {lang === 'ko' ? '데이터 없음' : 'No data'}
          </text>
        </svg>
      </div>
    );
  }

  // 각 세그먼트 계산
  const segments = [
    { key: 'go', value: data.go, color: STATUS_COLORS.go },
    { key: 'conditional', value: data.conditional, color: STATUS_COLORS.conditional },
    { key: 'holding', value: data.holding, color: STATUS_COLORS.holding },
    { key: 'no_go', value: data.no_go, color: STATUS_COLORS.no_go },
  ].filter(s => s.value > 0);

  let currentAngle = -90; // 12시 방향에서 시작

  const arcs = segments.map(segment => {
    const percentage = segment.value / total;
    const angle = percentage * 360;
    const startAngle = currentAngle;
    currentAngle += angle;

    // SVG arc 계산
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = ((startAngle + angle) * Math.PI) / 180;
    const midRad = ((startAngle + angle / 2) * Math.PI) / 180;
    
    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);

    // 툴팁 위치(세그먼트 중심 근처)
    const tooltipR = radius + strokeWidth / 2 + 6;
    const tx = center + tooltipR * Math.cos(midRad);
    const ty = center + tooltipR * Math.sin(midRad);
    
    const largeArcFlag = angle > 180 ? 1 : 0;
    
    const pathD = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;

    return {
      ...segment,
      pathD,
      percentage,
      tx,
      ty,
    };
  });

  const labels = STATUS_LABELS[lang as 'ko' | 'en'] || STATUS_LABELS.en;

  return (
    <div className="flex flex-col items-center">
      {/* 툴팁 영역(도넛 그래프 밖) */}
      <div className="min-h-[32px] flex items-center justify-center mb-2">
        {hovered ? (
          <div className="px-3 py-1.5 rounded-lg bg-black/70 border border-white/10 text-white text-xs">
            <span className="font-semibold">{hovered.label}</span>
            <span className="mx-1.5 text-white/60">•</span>
            <span className="font-bold">
              {lang === 'ko' ? `${hovered.value}건` : hovered.value}
            </span>
            <span className="mx-1.5 text-white/60">•</span>
            <span className="font-bold">{Math.round(hovered.percentage * 100)}%</span>
          </div>
        ) : (
          <div className="text-xs text-terminal-muted">
            {lang === 'ko' ? '세그먼트에 마우스를 올리면 상세가 보여요' : 'Hover a segment to see details'}
          </div>
        )}
      </div>

      <svg width={size} height={size} onMouseLeave={() => setHovered(null)}>
        {/* 배경 원 */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-terminal-dim/30"
        />
        
        {/* 세그먼트 */}
        {arcs.map((arc, i) => (
          <path
            key={arc.key}
            d={arc.pathD}
            fill="none"
            stroke={arc.color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            className="transition-all duration-500"
            style={{ 
              animationDelay: `${i * 100}ms`,
            }}
            onMouseEnter={() => {
              const key = arc.key as 'go' | 'conditional' | 'holding' | 'no_go';
              const label = labels[key] || key;
              setHovered({
                key,
                label,
                value: arc.value,
                percentage: arc.percentage,
                x: arc.tx,
                y: arc.ty,
              });
            }}
          />
        ))}
        
        {/* 중앙 텍스트 */}
        <text
          x={center}
          y={center - 8}
          textAnchor="middle"
          dominantBaseline="middle"
          className="text-2xl font-bold fill-current text-terminal-text"
        >
          {total}
        </text>
        <text
          x={center}
          y={center + 12}
          textAnchor="middle"
          dominantBaseline="middle"
          className="text-xs fill-current text-terminal-muted"
        >
          {lang === 'ko' ? '총 평가' : 'Total'}
        </text>
      </svg>
      
      {/* 범례 */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4">
        {segments.map(segment => (
          <div key={segment.key} className="flex items-center gap-2 text-sm">
            <span 
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: segment.color }}
            />
            <span className="text-terminal-muted">
              {labels[segment.key as keyof typeof labels]}
            </span>
            <span className="font-bold text-terminal-text ml-auto">
              {segment.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// 키워드 랭킹 컴포넌트 (수평 막대 그래프)
function KeywordRanking({ 
  keywords, 
  lang 
}: { 
  keywords: { keyword: string; count: number }[];
  lang: string;
}) {
  if (keywords.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-terminal-muted text-sm">
        {lang === 'ko' ? '키워드 없음' : 'No keywords'}
      </div>
    );
  }

  const maxCount = Math.max(...keywords.map(k => k.count));

  // 그라데이션 색상 (보라색 계열)
  const getBarColor = (index: number) => {
    const colors = [
      '#8b5cf6', // 1위 - 진한 보라
      '#a78bfa', // 2위
      '#c4b5fd', // 3위
      '#ddd6fe', // 4위
      '#ede9fe', // 5위 이하 - 연한 보라
    ];
    return colors[Math.min(index, colors.length - 1)];
  };

  return (
    <div className="space-y-3">
      {keywords.map((item, index) => {
        const widthPercent = (item.count / maxCount) * 100;
        const barColor = getBarColor(index);
        
        return (
          <div key={item.keyword} className="group">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                {/* 순위 뱃지 */}
                <span 
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                    index < 3 
                      ? 'bg-[#8b5cf6] text-white' 
                      : 'bg-terminal-dim/30 text-terminal-muted'
                  }`}
                >
                  {index + 1}
                </span>
                {/* 키워드 */}
                <span className="text-sm text-terminal-text font-medium truncate max-w-[180px]">
                  {item.keyword}
                </span>
              </div>
              {/* 횟수 */}
              <span className="text-sm font-bold text-terminal-muted">
                {item.count}
                <span className="text-xs ml-0.5">{lang === 'ko' ? '회' : 'x'}</span>
              </span>
            </div>
            {/* 프로그레스 바 */}
            <div className="h-2 bg-terminal-dim/20 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{ 
                  width: `${widthPercent}%`,
                  backgroundColor: barColor,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

